import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Trash, Plus, Pencil, Check, X } from 'lucide-react';
import { useSortableData } from '../../hooks/useSortableData';

export const ToDoGroupManagementModal = ({ isOpen, onClose, groups, onAdd, onUpdate, onDelete }) => {
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#000000');
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupData, setEditGroupData] = useState({ name: '', color: '' });

    const handleAddGroup = async () => {
        if (!newGroupName) return;
        await onAdd({ name: newGroupName, color: newGroupColor });
        setNewGroupName('');
        setNewGroupColor('#000000');
    };

    const startEditGroup = (group) => {
        setEditingGroupId(group.id);
        setEditGroupData({ name: group.name, color: group.color || '#000000' });
    };

    const saveEditGroup = async (id) => {
        await onUpdate(id, editGroupData);
        setEditingGroupId(null);
    };

    // Sorting
    const { items: sortedGroups } = useSortableData(groups);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage To Do Groups" maxWidth="max-w-2xl">
            <div className="flex flex-col h-[500px]">
                {/* Add Group Form */}
                <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 flex gap-4 items-end shrink-0">
                    <div className="flex-1 space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Group Name</label>
                        <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="e.g. Urgent, Testing" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Color</label>
                        <div className="flex gap-2">
                            <input
                                type="color"
                                value={newGroupColor}
                                onChange={(e) => setNewGroupColor(e.target.value)}
                                className="h-[38px] w-[60px] bg-transparent border border-stone-700 rounded cursor-pointer"
                            />
                        </div>
                    </div>
                    <PrimaryButton onClick={handleAddGroup} className="h-[38px] px-4"><Plus size={16} /> Add</PrimaryButton>
                </div>

                {/* Group List */}
                <div className="flex-1 overflow-y-auto space-y-2">
                    {sortedGroups.map((group) => (
                        <div key={group.id} className="flex items-center justify-between bg-stone-800/50 p-3 rounded border border-stone-700/50 hover:border-stone-600 transition">
                            {editingGroupId === group.id ? (
                                <div className="flex-1 flex gap-4 items-center mr-2">
                                    <Input
                                        value={editGroupData.name}
                                        onChange={(e) => setEditGroupData({ ...editGroupData, name: e.target.value })}
                                        className="h-8 text-xs flex-1"
                                    />
                                    <div className="flex gap-1">
                                        <input
                                            type="color"
                                            value={editGroupData.color}
                                            onChange={(e) => setEditGroupData({ ...editGroupData, color: e.target.value })}
                                            className="h-8 w-12 bg-transparent border border-stone-700 rounded cursor-pointer"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: group.color || 'gray' }} />
                                    <div className="text-stone-200 font-medium text-sm">{group.name}</div>
                                </div>
                            )}

                            <div className="flex items-center gap-1 shrink-0">
                                {editingGroupId === group.id ? (
                                    <>
                                        <button onClick={() => saveEditGroup(group.id)} className="text-green-500 hover:text-green-400 p-2 rounded hover:bg-stone-700/50 transition"><Check size={14} /></button>
                                        <button onClick={() => setEditingGroupId(null)} className="text-stone-500 hover:text-stone-400 p-2 rounded hover:bg-stone-700/50 transition"><X size={14} /></button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => startEditGroup(group)} className="text-stone-500 hover:text-blue-400 p-2 rounded hover:bg-stone-700/50 transition"><Pencil size={14} /></button>
                                        <button onClick={() => onDelete(group.id)} className="text-stone-500 hover:text-red-400 p-2 rounded hover:bg-stone-700/50 transition"><Trash size={14} /></button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                    {sortedGroups.length === 0 && (
                        <div className="text-center text-stone-500 py-8 italic">No groups defined.</div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
